import React from 'react';
import { Chip, ChipProps } from '@mui/material';
import {KpiContractUnits} from './Types';

export const MONTHS = ['jul', 'aug', 'sep', 'oct', 'nov', 'dec', 'jan', 'feb', 'mar', 'apr', 'may', 'jun'];

export const monthsTill = (monthToDate: string) => MONTHS.slice(0, MONTHS.indexOf(monthToDate) + 1);

const FORMATTED_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const formatMonth = (month: string) => {
    return FORMATTED_MONTHS.find(it => it.toLowerCase().startsWith(month));
};

const fmt = new Intl.NumberFormat("en", {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
})

export const formatKpiContactValue = (value: number, units: KpiContractUnits): string => {
    switch (units) {
        case 'Percentage':
            // Percentage as ###.#
            return  fmt.format(value).padStart(5);
        case 'Minutes':
            // Minutes as ###:##
            const secs = value % 1;
            const mins = value - secs;
            return `${mins.toString().padStart(3)}:${Math.round(secs * 60).toString().padStart(2, '0')}`;
        case 'Days':
            // Days as ##.#
            return  fmt.format(value).padStart(4);
    }
};

export const groupBy = <Key extends keyof any, T>(arr: T[], pred: (v: T) => Key): Map<Key, T[]> => {
    const grouped = new Map<Key, T[]>();
    arr.forEach((it) => {
        const index: Key = pred(it);
        if (Array.isArray(grouped.get(index))) {
            // tslint:disable-next-line:no-non-null-assertion
            const data = grouped.get(index)!;
            grouped.set(index, [...data, it]);
        } else {
            grouped.set(index, [it]);
        }
    });
    return grouped;
};

export const kpiCellColor = (satisfactoryLimit: number, unsatisfactoryLimit: number, value: number | null) => {
    const green = 'green' as const;
    const red = 'red' as const;
    const yellow = 'yellow' as const;

    if (value === null) {
        return green;
    } else if (satisfactoryLimit > unsatisfactoryLimit) {
        if (value >= satisfactoryLimit) {
            return green;
        } else if (value <= unsatisfactoryLimit) {
            return red;
        } else {
            return yellow;
        }
    } else if (unsatisfactoryLimit > satisfactoryLimit) {
        if (value <= satisfactoryLimit) {
            return green;
        } else if (value >= unsatisfactoryLimit) {
            return red;
        } else {
            return yellow;
        }
    } else if (value === 0) {
        return green;
    } else {
        return '';
    }
};

export const getPoorRange = (satisfactoryLimit: number, unsatisfactoryLimit: number, units: KpiContractUnits) => {
    let poor;
    if (Math.abs(satisfactoryLimit - unsatisfactoryLimit) <= 0.1) {
        poor = null;
    } else if (unsatisfactoryLimit < satisfactoryLimit) {
        poor = `${formatKpiContactValue(unsatisfactoryLimit, units)} - ${formatKpiContactValue(satisfactoryLimit, units)}`;
    } else {
        poor = `${formatKpiContactValue(satisfactoryLimit, units)} - ${formatKpiContactValue(unsatisfactoryLimit, units)}`;
    }
    return poor;
};

export type KpiCellColor = ReturnType<typeof kpiCellColor>

export const kpiCellColorToMuiChipColor = (cellColor: KpiCellColor) => {
    let color: 'success' | 'error' | 'warning' | 'default' = 'default'
    switch (cellColor) {
        case "green":
            color = 'success'
            break
        case "red":
            color = 'error'
            break
        case "yellow":
            color = 'warning'
            break
    }
    return color
}

interface ColoredKPIValueChipProps extends Omit<ChipProps, 'color' | 'label'> {
    value: string,
    color: KpiCellColor
    // for some reason, ChipProps doesn't contain this?
    component?: any
}

export const ColoredKPIValueChip = (props: ColoredKPIValueChipProps) => {
    const { color, value, sx, ...rest } = props
    const chipColor = kpiCellColorToMuiChipColor(color);
    return (
        <Chip
            {...rest}
            label={value}
            color={chipColor}
            sx={{
                ...sx,
                '&.MuiChip-colorWarning': {
                    backgroundColor: '#ffff00',
                    color: 'black'
                }
            }}
        />
    )
}
